import { ROTULO_DO_TEMA, TEMAS, type Tema } from '@aponte/shared';
import type { ReactNode } from 'react';
import { useTema } from '../tema/ContextoDeTema.js';

/**
 * Ícones em SVG, e não emoji nem caractere de sol e lua.
 *
 * `☀` e `🌙` viram desenho colorido em umas plataformas e glifo de texto em outras, com
 * tamanhos que não batem entre si — três botões lado a lado denunciariam a diferença. O
 * traço aqui é `currentColor`, então o ícone acompanha o realce do botão ativo e o ouro do
 * hover sem regra extra.
 */
const ICONE: Record<Tema, ReactNode> = {
  claro: (
    <>
      <circle cx="8" cy="8" r="3.1" />
      <path d="M8 1v1.6M8 13.4V15M15 8h-1.6M2.6 8H1M12.9 3.1l-1.1 1.1M4.2 11.8l-1.1 1.1M12.9 12.9l-1.1-1.1M4.2 4.2L3.1 3.1" />
    </>
  ),
  escuro: <path d="M13.2 10.3A5.6 5.6 0 0 1 5.7 2.8a5.6 5.6 0 1 0 7.5 7.5z" />,
  // Metade cheia, metade vazia: o mesmo símbolo de contraste que os sistemas usam para "automático".
  sistema: (
    <>
      <circle cx="8" cy="8" r="5.4" />
      <path d="M8 2.6a5.4 5.4 0 0 1 0 10.8z" fill="currentColor" stroke="none" />
    </>
  ),
};

/**
 * Escolha do tema: claro, escuro ou o que o sistema pedir.
 *
 * Três botões e não um interruptor: com "sistema" no meio, um botão que alterna não teria
 * como mostrar em que estado está — e a pessoa que segue o sistema precisa ver que segue.
 *
 * Só ícone, para caber ao lado das outras ações do cabeçalho sem empurrá-las para a linha
 * de baixo. O nome do tema continua existindo em `aria-label`, para leitor de tela, e em
 * `title`, para quem não reconhecer o desenho de cara.
 */
export function SeletorDeTema({ compacto = false }: { compacto?: boolean }) {
  const { tema, definirTema } = useTema();

  return (
    <div
      className={`seletor-tema${compacto ? ' compacto' : ''}`}
      role="group"
      aria-label="Tema da interface"
    >
      {TEMAS.map((opcao) => (
        <button
          key={opcao}
          type="button"
          className={opcao === tema ? 'ativo' : ''}
          aria-pressed={opcao === tema}
          aria-label={ROTULO_DO_TEMA[opcao]}
          title={ROTULO_DO_TEMA[opcao]}
          onClick={() => definirTema(opcao)}
        >
          <svg
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinecap="round"
            aria-hidden="true"
          >
            {ICONE[opcao]}
          </svg>
        </button>
      ))}
    </div>
  );
}
